import type { FC } from "react";
import { css } from "@stubhub/broadway-foundation/css";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { Heading } from "@stubhub/broadway-web";
import { useContent } from "src/shared/components/content";
import { UserGeneratedImage } from "src/website-apps/responsive/stubhub-performer/components/performer-info-card/user-generated-content/UserGeneratedImage";
import { CAROUSEL_SIZES } from "src/website-apps/shared/components/responsive-carousel/constants";
import { ResponsiveCarousel } from "src/website-apps/shared/components/responsive-carousel/ResponsiveCarousel";

const carouselWrapper = css({
  marginBlockStart: "primitives.100",
});

const carouselHeader = css({
  marginBlockEnd: "[10px]",
});

interface Props {
  /** Array of user image data */
  images: readonly string[];
  /** Category name for the user-generated images */
  categoryName: string;
  /** True when the carousel renders the sports fan-image treatment. */
  isSportsFanImages?: boolean;
}

/**
 * Component to display a carousel of user-generated images with a header.
 * @returns A box containing the carousel and its header
 */
export const UserContentCarousel: FC<Props> = ({ images, categoryName, isSportsFanImages = false }) => {
  const isMobile = !useMediaQuery(Breakpoint.Tablet);
  /** "Previous shows from fans" */
  const previousShowsFromFans = useContent("cid$#3560196#");
  /** "Previous events from fans" */
  const previousEventsFromFans = useContent("cid$#4408987#");

  return (
    <div className={carouselWrapper}>
      <Heading variant="title" size="small" className={carouselHeader}>
        {isSportsFanImages ? previousEventsFromFans : previousShowsFromFans}
      </Heading>

      <ResponsiveCarousel<string>
        size={CAROUSEL_SIZES.noSize}
        items={images}
        renderItem={(image) => <UserGeneratedImage key={image} userImage={image} categoryName={categoryName} />}
        hidePaginationButtons={isMobile}
        hasHoverEffect={false}
        gapPx={8}
      />
    </div>
  );
};
